import React, { useState } from 'react';
import { Box, IconButton, InputAdornment, TextField, TextFieldProps, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import RadioButtonUncheckedOutlinedIcon from '@mui/icons-material/RadioButtonUncheckedOutlined';
import { PASSWORD_REQUIREMENTS } from '../../utils/passwordPolicy';

/** Read by screen readers, not shown. */
const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

type PasswordFieldProps = Omit<TextFieldProps, 'type' | 'autoComplete' | 'variant' | 'id' | 'inputRef'> & {
  id: string;
  /** Password managers fill and save passwords by this. */
  autoComplete: 'current-password' | 'new-password';
  /**
   * Show the password rules as a checklist under the field. It appears on
   * first focus and stays, so the form doesn't jump as the field loses focus.
   */
  showRequirements?: boolean;
};

interface PasswordChecklistProps {
  id: string;
  password: string;
  /** After a failed submit, unmet rules are shown as problems. */
  showProblems: boolean;
}

/**
 * The shared password rules (the same ones the API enforces) as a checklist.
 * A rule only counts as met once something has been typed.
 */
export const PasswordChecklist: React.FC<PasswordChecklistProps> = ({ id, password, showProblems }) => (
  <Box
    component="ul"
    id={id}
    aria-label="Requirements for your password"
    sx={{
      listStyle: 'none',
      p: 0,
      m: 0,
      mt: 1,
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
      columnGap: 2,
      rowGap: 0.25,
    }}
  >
    {PASSWORD_REQUIREMENTS.map((requirement) => {
      const met = password.length > 0 && requirement.test(password);
      const problem = !met && showProblems;
      // Short rules sit two to a row; long ones (the symbol rules) get a row of their own.
      const fullRow = requirement.label.length > 24;
      return (
        <Box
          component="li"
          key={requirement.label}
          data-met={met ? 'true' : 'false'}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            minWidth: 0,
            gridColumn: fullRow ? '1 / -1' : undefined,
            color: problem ? 'error.main' : 'text.secondary',
          }}
        >
          {met ? (
            <CheckCircleOutlinedIcon aria-hidden="true" sx={{ fontSize: 16, mt: '3px', color: 'success.main' }} />
          ) : (
            <RadioButtonUncheckedOutlinedIcon
              aria-hidden="true"
              sx={{ fontSize: 16, mt: '3px', color: problem ? 'error.main' : 'grey.500' }}
            />
          )}
          <Typography component="span" variant="body2" sx={{ fontSize: '0.8125rem', color: 'inherit' }}>
            <Box component="span" sx={visuallyHidden}>
              {met ? 'Done: ' : 'Needed: '}
            </Box>
            {requirement.label}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

/**
 * The one password input for sign in, sign up, editor registration and
 * password reset: a show/hide toggle, the right autocomplete hint, and (for
 * new passwords) the requirements checklist instead of a strength meter.
 */
const PasswordField = React.forwardRef<HTMLInputElement, PasswordFieldProps>(function PasswordField(
  { id, showRequirements = false, onFocus, inputProps, InputProps, error, helperText, value, ...rest },
  ref
) {
  const [visible, setVisible] = useState(false);
  const [focusedOnce, setFocusedOnce] = useState(false);
  const password = typeof value === 'string' ? value : '';
  const checklistShown = showRequirements && (focusedOnce || password.length > 0 || Boolean(error));
  const requirementsId = `${id}-requirements`;
  const describedBy = [helperText ? `${id}-helper-text` : null, checklistShown ? requirementsId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <Box>
      <TextField
        {...rest}
        id={id}
        inputRef={ref}
        value={value}
        type={visible ? 'text' : 'password'}
        error={error}
        helperText={helperText}
        onFocus={(event) => {
          setFocusedOnce(true);
          onFocus?.(event);
        }}
        inputProps={{
          ...inputProps,
          'aria-describedby': describedBy || undefined,
        }}
        InputProps={{
          ...InputProps,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={visible ? 'Hide password' : 'Show password'}
                onClick={() => setVisible((current) => !current)}
                onMouseDown={(event) => event.preventDefault()}
                edge="end"
                size="small"
              >
                {visible ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {checklistShown && <PasswordChecklist id={requirementsId} password={password} showProblems={Boolean(error)} />}
    </Box>
  );
});

export default PasswordField;
